import { itx } from "../translationItx/itx";
import BaseDialog from "./BaseDialog";
import type Signal from "../models/Signal";
import { useState } from "react";
import Channel from "../models/Channel";
import BaseObject from "../models/BaseObject";

export default async function showSignalConnectionDialog(dragSignal: Signal, dropSignal: Signal) {
  await state.dialogManager.openDialog(<InnerDialog dragSignal={dragSignal} dropSignal={dropSignal} />, true);
}

// ─── Option card ─────────────────────────────────────────────────────────────

function OptionCard(props: {
  icon: string;
  label: string;
  subLabel?: string;
  selected: boolean;
  onClick: () => void;
  existingAddresses?: boolean;
}) {
  const { icon, label, subLabel, selected, onClick, existingAddresses } = props;
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 14px",
        borderRadius: "8px",
        border: `2px solid ${selected ? "var(--primary-color)" : "rgba(0,0,0,0.12)"}`,
        backgroundColor: existingAddresses ? "rgba(240, 11, 11, 0.16)" : selected ? "rgba(0,0,0,0.03)" : "transparent",
        cursor: "pointer",
        transition: "border-color 0.15s, background-color 0.15s",
      }}
      data-tooltip-id="tv-tooltip"
      data-tooltip-content={
        existingAddresses
          ? itx(
              "showSignalConnectionDialog.existingAddress",
              "Diese Gruppenadresse ist bereits mit dem Signal verknüpft",
            )
          : ""
      }
      data-tooltip-place="bottom"
    >
      <i
        className={`las ${icon}`}
        style={{ fontSize: "20px", color: selected ? "var(--primary-color)" : "#888", flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: "14px" }}>{label}</div>
        {subLabel && <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>{subLabel}</div>}
      </div>
      {selected && (
        <i className="las la-check-circle" style={{ fontSize: "18px", color: "var(--primary-color)", flexShrink: 0 }} />
      )}
    </div>
  );
}

// ─── Inner dialog ─────────────────────────────────────────────────────────────

function InnerDialog(props: { dragSignal: Signal; dropSignal: Signal }) {
  const { dragSignal, dropSignal } = props;
  const [action, setAction] = useState("createNew");

  type OptionItem = { label: string; subLabel?: string; value: string; isNew: boolean; existingAddresses?: boolean };

  const options: OptionItem[] = [
    {
      label: itx("showSignalConnectionDialog.createNewGroupAddress", "Neue Gruppenadresse"),
      value: "createNew",
      isNew: true,
      existingAddresses: false,
    },
  ];
  dragSignal.groupAddresses.forEach((ga) => {
    let existingAddresses = false;
    dropSignal.groupAddresses.forEach((ga2) => {
      if (ga.fullAddress === ga2.fullAddress) {
        existingAddresses = true;
      }
    });
    options.push({
      label: ga.fullAddress,
      subLabel: ga.name || undefined,
      value: ga.fullAddress,
      isNew: false,
      existingAddresses: existingAddresses,
    });
  });

  const hasExistingAddresses = options.length > 1;
  let dropConnectedFunction = new Array<BaseObject>();
  (dropSignal.parent as Channel).connections.forEach((obj) => {
    let connectedObj = obj.getPartner(dropSignal.parent as Channel);
    if (connectedObj) {
      dropConnectedFunction.push(connectedObj);
    }
  });

  function ConnectetElements(props: { sig: Signal }) {
    const { sig } = props;
    const channel = sig.parent as Channel;
    let connectedFunction = new Array<BaseObject>();
    channel.connections.forEach((obj) => {
      let connectedObj = obj.getPartner(channel);
      if (connectedObj) {
        connectedFunction.push(connectedObj);
      }
    });
    if (connectedFunction.length == 0) {
      return null;
    }
    return (
      <div style={{ fontSize: "9px", color: "#888", marginTop: "2px" }}>
        {itx("showSignalConnectionDialog.connectedConsumer", "Verknüpft mit:")}
        {connectedFunction.map((obj, index) => (
          <div key={index} style={{ fontSize: "9px", color: "#888", marginTop: "2px" }}>
            {itx("components." + obj.className, obj.className)} {obj.fullTag} {obj.name} <br />
          </div>
        ))}
      </div>
    );
  }
  function getErrorText(dragSignal: Signal, dropSignal: Signal): string {
    let errorText = "";
    const dpt1 = dragSignal.knxDatapointType;
    const dpt2 = dropSignal.knxDatapointType;
    if (dpt1 != dpt2) {
      errorText = itx(
        "showSignalConnectionDialog.dptMismatch",
        "Die KNX-Datentypen der Signale stimmen nicht überein. (T1 vs T2)",
        { T1: dpt1, T2: dpt2 },
      );
    }
    return errorText;
  }
  let errorText = getErrorText(dragSignal, dropSignal);

  return (
    <BaseDialog
      title={itx("showSignalConnectionDialog.title", "Signal Verknüpfen")}
      onOk={() => {
        if (action === "createNew") {
          dragSignal.createAndConnectWithNewGroupAddress(dropSignal);
        } else {
          const ga = dragSignal.groupAddresses.find((ga) => ga.fullAddress === action);
          if (ga) {
            dropSignal.connectWithGroupAddress(ga);
          }
        }
        return true;
      }}
    >
      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "16px", minWidth: "340px" }}>
        {/* Connection context: requestSignal → signal */}
        <div
          style={{
            alignItems: "center",
            borderRadius: "8px",
            backgroundColor: errorText == "" ? "rgba(0,0,0,0.04)" : "rgba(240, 11, 11, 0.16)",
          }}
        >
          {errorText && (
            <div
              style={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                alignSelf: "stretch",
                padding: "10px 14px",
              }}
            >
              <img style={{ height: "30px", width: "auto" }} src={state.hostBaseUrl + "images/warning.png"} />
              {errorText}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 14px",
              fontSize: "13px",
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: "var(--primary-color)" }}>
                {dragSignal.fullTag} | {dragSignal.knxDatapointType}
              </div>

              <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                {dragSignal.name + " " + dragSignal.knxFunctionText}
              </div>

              <ConnectetElements sig={dragSignal} />
            </div>
            <i className="las la-long-arrow-alt-right" style={{ fontSize: "22px", color: "#bbb", flexShrink: 0 }} />
            <div style={{ flex: 1, textAlign: "right" }}>
              <div style={{ fontWeight: 600 }}>
                {dropSignal.fullTag} | {dropSignal.knxDatapointType}
              </div>

              <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                {dropSignal.name + " " + dropSignal.knxFunctionText}
              </div>

              <ConnectetElements sig={dropSignal} />
            </div>
          </div>
        </div>

        {/* Question text */}
        <div style={{ fontSize: "13px", color: "#666" }}>
          {hasExistingAddresses
            ? itx(
                "showSignalConnectionDialog.howToKonnectWithSignal",
                "Welche Gruppenadresse soll mit dem Signal verknüpft werden?",
              )
            : itx(
                "showSignalConnectionDialog.noGroupAddress",
                "Soll eine neue Gruppenadresse erstellt werden? Diese wird automatisch mit dem Signal verknüpft.",
              )}
        </div>

        {/* Selection cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {options.map((opt) => (
            <OptionCard
              key={opt.value}
              icon={opt.existingAddresses ? "las la-times" : opt.isNew ? "la-plus-circle" : "la-link"}
              label={opt.label}
              subLabel={opt.subLabel}
              selected={action === opt.value}
              existingAddresses={opt.existingAddresses}
              onClick={() => {
                if (!opt.existingAddresses) {
                  setAction(opt.value);
                }
              }}
            />
          ))}
        </div>
      </div>
    </BaseDialog>
  );
}
